/**
 * Plays a short sound when a "Complete the set" row is pointed at, in step
 * with the image that slides out beside it.
 *
 * Three constraints shape this:
 *
 * 1. Browsers refuse to start audio until the page has been interacted with.
 *    Hovering is not an interaction, so the element stays silent until the
 *    first click or key press, then plays from that point on. This is browser
 *    policy — there is no way to opt out of it — so it degrades to silence
 *    rather than to an error.
 * 2. It is bound to the same condition as the reveal itself: a real pointer on
 *    a wide screen. On touch every image is already visible, so there is no
 *    moment for a sound to belong to.
 * 3. Sweeping down the list crosses several rows in a few hundred
 *    milliseconds. Each new row restarts the clip rather than layering another
 *    copy on top, and a short floor stops a jittery pointer from machine-gunning
 *    it.
 */

const REVEAL_MEDIA = '(hover: hover) and (min-width: 750px)';

/** Shortest gap between two plays, in ms. */
const MIN_INTERVAL = 70;

class NoriSetSound extends HTMLElement {
  /** @type {HTMLAudioElement | null} */
  #audio = null;
  /** @type {Element | null} */
  #currentRow = null;
  /** @type {MediaQueryList | null} */
  #reveal = null;
  #armed = false;
  #lastPlayed = 0;

  connectedCallback() {
    if (this.dataset.enabled !== 'true' || !this.dataset.src) return;

    this.#reveal = matchMedia(REVEAL_MEDIA);

    this.addEventListener('pointerover', this.#handlePointerOver);
    this.addEventListener('pointerleave', this.#handlePointerLeave);

    // Deliberately on the document: the visitor almost never clicks this list
    // itself, so waiting for a gesture here would keep it muted forever.
    document.addEventListener('pointerdown', this.#arm, { once: true, passive: true });
    document.addEventListener('keydown', this.#arm, { once: true });
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.#handlePointerOver);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    document.removeEventListener('pointerdown', this.#arm);
    document.removeEventListener('keydown', this.#arm);
    this.#audio?.pause();
    this.#audio = null;
  }

  /**
   * Loading is deferred to the first gesture rather than done on connect, so
   * visitors who never trigger the effect never pay for the file.
   */
  #arm = () => {
    this.#armed = true;
    if (this.#audio || !this.#reveal?.matches) return;
    this.#audio = new Audio(this.dataset.src);
    this.#audio.preload = 'auto';
    this.#audio.volume = this.#volume;
  };

  get #volume() {
    const raw = Number.parseInt(this.dataset.volume ?? '', 10);
    if (Number.isNaN(raw)) return 0.6;
    return Math.min(Math.max(raw, 0), 100) / 100;
  }

  /**
   * pointerover rather than pointerenter: it bubbles, so one listener covers
   * every row. It also fires when moving between a row's own children, hence
   * comparing against the row last played.
   */
  #handlePointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const row = target.closest('.nori-set__row');
    if (!row || row === this.#currentRow) return;

    this.#currentRow = row;
    this.#play();
  };

  #handlePointerLeave = () => {
    // Cleared on the way out, so returning to the same row plays again.
    this.#currentRow = null;
  };

  #play() {
    if (!this.#armed || !this.#reveal?.matches) return;

    this.#arm();
    const audio = this.#audio;
    if (!audio) return;

    const now = performance.now();
    if (now - this.#lastPlayed < MIN_INTERVAL) return;
    this.#lastPlayed = now;

    audio.volume = this.#volume;
    audio.currentTime = 0;
    // Can still be refused (a per-site sound block, a policy this page has not
    // satisfied). Silence is the correct outcome, not a console error.
    audio.play().catch(() => {});
  }
}

if (!customElements.get('nori-set-sound')) {
  customElements.define('nori-set-sound', NoriSetSound);
}
